import { useMemo, useCallback } from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { getCalendarEvents } from "../services/agendaApi";
import { sortEventsJoinedFirst } from "../utils/agendaUtils";

export default function useAgendaEvents({
  entityType = "event,webinar",
  event_type,
  perPage = 15,
  collection = "calendar",
} = {}) {
  const queryClient = useQueryClient();
  const effectiveEntityType = event_type || entityType || "event,webinar";
  const effectiveCollection =
    collection === "saved" || collection === "completed"
      ? collection
      : "calendar";
  const queryKey = useMemo(
    () => ["agendaCalendarEvents", effectiveCollection, effectiveEntityType, perPage],
    [effectiveCollection, effectiveEntityType, perPage]
  );

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) =>
      getCalendarEvents({
        entityType: effectiveEntityType,
        event_type: effectiveEntityType,
        page: pageParam,
        perPage,
        collection: effectiveCollection,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.pagination;
      const currentPage = Number(
        pagination?.currentPage || allPages?.length || 1
      );
      const lastPageNum = Number(pagination?.lastPage || 1);

      if (pagination?.nextPageUrl) {
        return currentPage + 1;
      }
      if (currentPage < lastPageNum) {
        return currentPage + 1;
      }
      return undefined;
    },
    refetchOnWindowFocus: false,
    keepPreviousData: false,
  });

  const listEvents = useMemo(() => {
    const rawEvents = data?.pages?.flatMap((page) => page?.data || []) || [];
    const seen = new Set();
    const uniqueEvents = [];
    for (const ev of rawEvents) {
      const uniqueKey =
        ev?.id ||
        (ev
          ? `${ev.type || ev.entity_type}-${ev.apiId || ev.raw_id || Math.random()}`
          : null);
      if (uniqueKey && !seen.has(uniqueKey)) {
        seen.add(uniqueKey);
        uniqueEvents.push(ev);
      } else if (!uniqueKey && ev) {
        uniqueEvents.push(ev);
      }
    }
    return sortEventsJoinedFirst(uniqueEvents);
  }, [data]);

  const upcomingEvents = useMemo(() => {
    const rawUpcoming = data?.pages?.[0]?.upcoming || [];
    const seen = new Set();
    const uniqueEvents = [];
    for (const ev of rawUpcoming) {
      const uniqueKey =
        ev?.id ||
        (ev
          ? `${ev.type || ev.entity_type}-${ev.apiId || ev.raw_id || Math.random()}`
          : null);
      if (uniqueKey && !seen.has(uniqueKey)) {
        seen.add(uniqueKey);
        uniqueEvents.push(ev);
      } else if (!uniqueKey && ev) {
        uniqueEvents.push(ev);
      }
    }
    return uniqueEvents;
  }, [data]);

  const suggestionEvents = useMemo(() => {
    return data?.pages?.[0]?.suggestion || [];
  }, [data]);

  const ads = useMemo(() => {
    return data?.pages?.[0]?.ads || null;
  }, [data]);

  const total = useMemo(() => {
    return data?.pages?.[0]?.pagination?.total || listEvents.length;
  }, [data, listEvents.length]);

  const loadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const refreshEvents = useCallback(() => {
    queryClient.invalidateQueries(queryKey);
    refetch();
  }, [queryClient, queryKey, refetch]);

  const prependEvent = useCallback(
    (newEvent) => {
      if (!newEvent) return;
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData || !oldData.pages || oldData.pages.length === 0) {
          return {
            pages: [
              {
                status: true,
                data: [newEvent],
                upcoming: [newEvent],
                suggestion: [],
                ads: null,
                pagination: { currentPage: 1, lastPage: 1, total: 1 },
              },
            ],
            pageParams: [1],
          };
        }
        const firstPage = oldData.pages[0];
        const newFirstPage = {
          ...firstPage,
          data: [newEvent, ...(firstPage.data || [])],
          upcoming: [newEvent, ...(firstPage.upcoming || [])],
        };
        return {
          ...oldData,
          pages: [newFirstPage, ...oldData.pages.slice(1)],
        };
      });
    },
    [queryClient, queryKey]
  );

  const replaceEvent = useCallback(
    (updatedEvent) => {
      if (!updatedEvent) return;
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData || !oldData.pages) return oldData;
        const newPages = oldData.pages.map((p) => ({
          ...p,
          data: (p.data || []).map((ev) =>
            String(ev.id) === String(updatedEvent.id) ? updatedEvent : ev
          ),
          upcoming: (p.upcoming || []).map((ev) =>
            String(ev.id) === String(updatedEvent.id) ? updatedEvent : ev
          ),
        }));
        return {
          ...oldData,
          pages: newPages,
        };
      });
    },
    [queryClient, queryKey]
  );

  const removeEvent = useCallback(
    (eventId) => {
      if (!eventId) return;
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData || !oldData.pages) return oldData;
        const newPages = oldData.pages.map((p) => ({
          ...p,
          data: (p.data || []).filter(
            (ev) => String(ev.id) !== String(eventId)
          ),
          upcoming: (p.upcoming || []).filter(
            (ev) => String(ev.id) !== String(eventId)
          ),
        }));
        return {
          ...oldData,
          pages: newPages,
        };
      });
    },
    [queryClient, queryKey]
  );

  return {
    events: listEvents,
    listEvents,
    upcomingEvents,
    suggestionEvents,
    ads,
    total,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    hasMore: Boolean(hasNextPage),
    loadMore,
    fetchNextPage,
    refreshEvents,
    prependEvent,
    replaceEvent,
    removeEvent,
  };
}
